import type { AxiosError } from 'axios';
import { useEffect, useRef } from 'react';

import { hasValue } from 'utils/hasValue';

import { getAxiosInstance } from 'api/utils';

import {
  REFRESH_TOKEN_PATH,
  useAuthenticationRefreshToken
} from './useAuthenticationRefreshToken';

declare module 'axios' {
  interface InternalAxiosRequestConfig {
    /**
     * Set once a request has been retried after a token refresh,
     * so a second 401 is rejected instead of refreshing again
     */
    _retriedAfterRefresh?: boolean;
  }
}

export const useCustomerApiAuthInterceptor = () => {
  const { refreshAccessToken } = useAuthenticationRefreshToken();

  /**
   * In-flight refresh shared by concurrent 401s, so a burst of failing
   * requests triggers a single refresh
   */
  const refreshPromiseRef = useRef<Promise<string | undefined> | null>(null);

  /**
   * Effect intercepting Axios responses
   * In case of a response being denied, refresh access token and retry the request once
   */
  useEffect(() => {
    const refreshOnce = () => {
      refreshPromiseRef.current ??= refreshAccessToken().finally(() => {
        refreshPromiseRef.current = null;
      });

      return refreshPromiseRef.current;
    };

    const interceptorResponseId = getAxiosInstance().interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const { config, response } = error;

        if (!hasValue(response)) {
          return Promise.reject(error);
        }

        const canRetry =
          response.status === 401 &&
          hasValue(config) &&
          !config._retriedAfterRefresh &&
          config.url !== REFRESH_TOKEN_PATH;

        if (canRetry) {
          const token = await refreshOnce();

          if (hasValue(token)) {
            config._retriedAfterRefresh = true;
            config.headers.Authorization = `Bearer ${token}`;

            return getAxiosInstance()(config);
          }
        }

        return Promise.reject(response.data);
      }
    );

    return () => {
      getAxiosInstance().interceptors.response.eject(interceptorResponseId);
    };
  }, [refreshAccessToken]);
};
